import { useState } from "react";
import { Button, Modal, Stack } from "@salesapp/ui";
import { useAccess } from "../../shell/AccessProvider";
import { crmApi } from "../../lib/crmApi";

const RECYCLE_NOTE =
  "Deleted records sit in the recycle bin for 30 days. Restore them from Organization settings → Recycle bin.";

export function CompanyDeleteButton({
  orgId,
  companyId,
  recordLabel,
  onDeleted,
  size = "md",
}: {
  orgId: string;
  companyId: string;
  recordLabel: string;
  onDeleted: () => Promise<void> | void;
  size?: "sm" | "md";
}) {
  const { can } = useAccess();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<Array<{ key: string; label: string; count: number }>>([]);

  if (!can("crm.companies", "delete")) return null;

  async function openConfirm() {
    setError(null);
    setBusy(true);
    try {
      const result = await crmApi.companyDependents(companyId, orgId);
      setItems(result.items);
      setOpen(true);
    } catch (err) {
      setItems([]);
      setError(err instanceof Error ? err.message : "Linked records could not be checked.");
      setOpen(true);
    } finally {
      setBusy(false);
    }
  }

  async function confirmDelete() {
    setBusy(true);
    setError(null);
    try {
      await crmApi.deleteCompany(companyId, orgId, true);
      setOpen(false);
      await onDeleted();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete company");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Button type="button" variant="danger" size={size} disabled={busy && !open} onClick={() => openConfirm()}>
        {busy && !open ? "Checking…" : "Delete"}
      </Button>
      <Modal
        isOpen={open}
        onClose={() => {
          if (!busy) setOpen(false);
        }}
        title={`Delete ${recordLabel}?`}
        footer={(
          <>
            <Button type="button" variant="secondary" disabled={busy} onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="button" variant="danger" disabled={busy} onClick={() => confirmDelete()}>
              {busy ? "Deleting…" : items.length ? "Yes, delete all those items" : "Yes, delete"}
            </Button>
          </>
        )}
      >
        <Stack gap={3}>
          {error ? <p className="crm-error" role="alert">{error}</p> : null}
          {items.length > 0 ? (
            <>
              <p>This company still has assigned items linked to it:</p>
              <ul className="crm-delete-links">
                {items.map((item) => (
                  <li key={item.key}>
                    {item.label}: {item.count}
                  </li>
                ))}
              </ul>
              <p>Do you want to delete all those items?</p>
            </>
          ) : (
            <p>No other records are linked to this company.</p>
          )}
          <p className="crm-muted">{RECYCLE_NOTE}</p>
        </Stack>
      </Modal>
    </>
  );
}
